import { getAxiosInstance } from './api'

/**
 * Referral program API (backend: beta-backend src/routes/referral.route.ts,
 * docs/REFERRAL_PROGRAM.md). $LEET is tracked off-chain for now — rewards
 * accrue and become claimable at token launch (`program.claimsOpen`).
 */

export type CommunityReferralStatus =
  | 'pending_event'
  | 'review'
  | 'claimable'
  | 'claim_requested'
  | 'claimed'
  | 'expired'
  | 'rejected'

export type ReferralStage =
  | 'community_registered'
  | 'event_created'
  | 'event_active'
  | 'event_completed'
  | Exclude<CommunityReferralStatus, 'pending_event'>

export interface ReferralTotals {
  totalReferrals: number
  activeReferrals: number
  successfulCommunities: number
  inReview: number
  expiredReferrals: number
  rejectedReferrals: number
  verifiedPlayersReferred: number
  leet: { earned: number; pending: number; claimed: number; inReview: number }
}

export interface ReferralSummary {
  code: string
  points: { balance: number; lifetime: number }
  referredUsers: number
  referredBy: { username: string; avatar?: { url?: string }; at: string } | null
  breakdown: Record<string, { points: number; count: number }>
  pointsTable: Record<string, number>
  claimWindowDays: number
  communities: ReferralTotals
}

export interface CommunityReferral {
  _id: string
  communityId: string
  community?: { _id: string; community_name: string; community_url: string; avatar?: { url?: string } }
  status: CommunityReferralStatus
  stage: ReferralStage
  source: 'code_at_creation' | 'owner_referred_by'
  referredAt: string
  attributionExpiresAt: string
  firstEvent?: { eventType?: 'tournament'; id?: string; name?: string; startedAt?: string; endedAt?: string }
  registeredCount: number
  verifiedCount: number
  disqualifiedCount: number
  reward: { amount: number; currency: 'LEET'; curveVersion?: string }
  claim?: { requestedAt?: string; claimedAt?: string; txHash?: string }
}

export interface ReferralProgram {
  curveVersion: string
  rewardCurve: { participants: number; leet: number }[]
  minParticipants: number
  attributionWindowDays: number
  currency: 'LEET'
  claimsOpen: boolean
}

export interface LeaderboardRow {
  username: string
  avatar?: { url?: string }
  verifiedPlayers: number
  communities: number
}

export interface AdminCommunityReferral extends Omit<CommunityReferral, 'stage' | 'community' | 'communityId'> {
  referrerId?: { _id: string; username: string; email?: string }
  communityId?: { _id: string; community_name: string; community_url: string }
  communityOwnerId?: { _id: string; username: string }
  reviewFlags: string[]
  review?: { decidedAt?: string; note?: string }
  claim?: { requestedAt?: string; walletAddress?: string; claimedAt?: string; txHash?: string }
}

const api = () => getAxiosInstance()

export const referralApi = {
  getMe: async (): Promise<ReferralSummary> => (await api().get('/referrals/me')).data,
  getCommunities: async (): Promise<CommunityReferral[]> =>
    (await api().get('/referrals/me/communities')).data.referrals ?? [],
  getProgram: async (): Promise<ReferralProgram> => (await api().get('/referrals/program')).data,
  getLeaderboard: async (limit = 10): Promise<LeaderboardRow[]> =>
    (await api().get('/referrals/leaderboard', { params: { limit } })).data.leaderboard ?? [],
  lookup: async (code: string): Promise<{ username: string; avatar?: { url?: string } } | null> => {
    try {
      return (await api().get(`/referrals/code/${encodeURIComponent(code)}`)).data.referrer ?? null
    } catch {
      return null
    }
  },
  claim: async (code: string) => (await api().post('/referrals/claim', { code })).data,
  requestCommunityClaim: async (id: string) => (await api().post(`/referrals/communities/${id}/claim`)).data,

  admin: {
    list: async (status?: string): Promise<AdminCommunityReferral[]> =>
      (await api().get('/admin/referrals/communities', { params: status ? { status } : {} })).data.referrals ?? [],
    review: async (id: string, decision: 'approve' | 'reject', note?: string) =>
      (await api().patch(`/admin/referrals/communities/${id}/review`, { decision, note })).data,
    markClaimed: async (id: string, txHash: string) =>
      (await api().patch(`/admin/referrals/communities/${id}/mark-claimed`, { txHash })).data,
    runSweep: async () => (await api().post('/admin/referrals/communities/sweep')).data.result,
  },
}

export const referralLink = (code: string) => `${window.location.origin}/r/${code}`
